import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Lock, X, Image as ImageIcon, BadgeCheck, Check, RotateCw, FileImage } from "lucide-react";
import axios from "axios";
import { setDesignCover, getDesignRenders, api } from "../lib/api";
import { toast } from "sonner";
import TosConsentCheckbox from "./TosConsentCheckbox";

/**
 * V1.7 — AI Renders panel. Studio-tier paywall feature: one credit unlocks
 * three photorealistic shots (exterior + living + bedroom). Renders a
 * locked-card upsell when the user has no credits, then swaps to a 3-shot
 * grid after purchase. Image generation itself is queued server-side; we
 * surface a friendly "rendering soon" state until V1.7.1 wires it.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIRendersPanel({ open, onClose, designId, onCheckout, credits, onCoverSet }) {
  const [loading, setLoading] = useState(false);
  const [shots, setShots] = useState(null);
  const [paywall, setPaywall] = useState(null);
  // V2.0.20 — ToS audit-row id required before the Studio-tier checkout
  // button enables on the paywall card. Reset on every modal close.
  const [tosAcceptanceId, setTosAcceptanceId] = useState(null);
  const tosAccepted = !!tosAcceptanceId;
  useEffect(() => {
    if (!open) setTosAcceptanceId(null);
  }, [open]);
  // V1.7.3 — Set-as-cover state. Keyed by shot index so the button on each
  // tile can show its own loading/success state without conflating.
  const [coverState, setCoverState] = useState({}); // { [idx]: "idle" | "saving" | "saved" }
  // V1.7.5 — whether the current `shots` came from a fresh Generate (so the
  // confirmation panel reads "renders ready") or from the persisted gallery
  // (so the title reads "saved gallery" + a Regenerate button appears).
  const [fromGallery, setFromGallery] = useState(false);

  // V2.0.87 — Reference-image toggle. When ON, the AI Render call
  // threads the user's most-recent uploaded floor-plan sketch into
  // every shot via `use_latest_reference: true`. We fetch the source
  // files on modal open to decide whether to render the checkbox at
  // all (no images uploaded = no toggle). V2.0.87.1 — the toggle also
  // surfaces when a legacy image upload has no thumbnail_data_url;
  // it just shows a file-icon placeholder instead of a thumbnail.
  // V2.0.87.2 — added multi-image selection (up to 3). When the user
  // has more than one image upload, we render a selectable grid that
  // posts `reference_indices` in the body instead of `use_latest_reference`.
  const [useReference, setUseReference] = useState(true);
  const [referenceThumb, setReferenceThumb] = useState(null);
  const [referenceFilename, setReferenceFilename] = useState(null);
  const [hasImageUpload, setHasImageUpload] = useState(false);
  const [imageUploads, setImageUploads] = useState([]); // [{index, filename, thumb, mime}]
  const [selectedIndices, setSelectedIndices] = useState([]); // subset of file indices, max 3
  useEffect(() => {
    if (!open || !designId) {
      setReferenceThumb(null); setReferenceFilename(null); setHasImageUpload(false);
      setImageUploads([]); setSelectedIndices([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/designs/${designId}/source-files`);
        if (cancel) return;
        const imgs = (r.data?.files || []).filter((f) => (f.mime || "").startsWith("image/"));
        const latest = imgs[imgs.length - 1] || null;
        setHasImageUpload(!!latest);
        setReferenceThumb(latest?.thumbnail_data_url || null);
        setReferenceFilename(latest?.filename || null);
        setImageUploads(imgs.map((f) => ({
          index: f.index,
          filename: f.filename,
          thumb: f.thumbnail_data_url,
          mime: f.mime,
        })));
        // Default: pick the latest single image (matches use_latest_reference).
        setSelectedIndices(latest ? [latest.index] : []);
      } catch (err) {
        if (!cancel) {
          setReferenceThumb(null); setReferenceFilename(null); setHasImageUpload(false);
          setImageUploads([]); setSelectedIndices([]);
        }
      }
    })();
    return () => { cancel = true; };
  }, [open, designId]);

  const toggleIndex = (idx) => {
    setSelectedIndices((prev) => {
      if (prev.includes(idx)) return prev.filter((i) => i !== idx);
      if (prev.length >= 3) {
        // Ring-buffer semantics: drop the oldest selection, add the new one.
        return [...prev.slice(1), idx];
      }
      return [...prev, idx];
    });
  };

  // V1.7.5 — Auto-load the persisted gallery the first time the modal
  // opens for a given design. No credit consumed; we just GET /renders.
  useEffect(() => {
    if (!open || !designId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getDesignRenders(designId);
        const saved = r.data?.shots || [];
        if (!cancelled && saved.length > 0) {
          setShots(saved);
          setFromGallery(true);
        }
      } catch (_) { /* silent — fall back to empty unlock card */ }
    })();
    return () => { cancelled = true; };
  }, [open, designId]);

  if (!open) return null;

  const handleSetCover = async (i, dataUrl) => {
    setCoverState((s) => ({ ...s, [i]: "saving" }));
    try {
      await setDesignCover(designId, dataUrl);
      setCoverState((s) => ({ ...s, [i]: "saved" }));
      toast.success("Set as project cover.");
      onCoverSet?.(dataUrl);
    } catch (e) {
      setCoverState((s) => ({ ...s, [i]: "idle" }));
      toast.error("Could not set cover. Try again.");
    }
  };

  const handleUnlock = async () => {
    setLoading(true);
    setPaywall(null);
    try {
      const body = {};
      // V2.0.87.2 — When the user has selected specific images via the
      // multi-image UI, prefer `reference_indices`. Otherwise fall back
      // to the checkbox's `use_latest_reference` shortcut.
      if (useReference && hasImageUpload) {
        if (imageUploads.length > 1 && selectedIndices.length > 0) {
          body.reference_indices = selectedIndices.slice(0, 3);
        } else {
          body.use_latest_reference = true;
        }
      }
      const r = await axios.post(
        `${API}/designs/${designId}/renders`,
        body,
        { withCredentials: true },
      );
      const newShots = r.data.shots || [];
      setShots(newShots);
      setFromGallery(false);
      // V1.7.4 — Auto-set the first ready Exterior shot as the project
      // cover. Silent (no toast) — the user gets the dashboard upgrade for
      // free as a generation side-effect. Manual Set-as-Cover still
      // available for users who want a different shot.
      const heroIdx = newShots.findIndex(
        (s) => s.status === "ready" && s.data_url && /exterior/i.test(s.label || ""),
      );
      if (heroIdx !== -1) {
        try {
          await setDesignCover(designId, newShots[heroIdx].data_url);
          setCoverState((s) => ({ ...s, [heroIdx]: "saved" }));
          onCoverSet?.(newShots[heroIdx].data_url);
        } catch (_) { /* silent */ }
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall(e.response.data?.detail || { price_usd: 19 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ai-renders-panel"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-zinc-950 border-2 border-brass-700 shadow-[0_20px_80px_rgba(0,0,0,0.7)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="ai-renders-close"
          aria-label="Close"
          className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-zinc-100"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
            // Studio Tier · AI Renders
          </div>
          <h2 className="font-display text-2xl text-zinc-100 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brass-400" />
            Imagine your home in photos
          </h2>
          <p className="text-zinc-400 text-sm mt-1.5">
            Three photorealistic shots — exterior hero, living room, primary bedroom — rendered from
            your plan.
          </p>
        </div>

        <div className="p-5">
          {/* Paywall card */}
          {paywall && !shots && (
            <div
              data-testid="ai-renders-paywall"
              className="border-2 border-orange-500/60 bg-orange-950/20 p-5 text-center"
            >
              <Lock className="w-7 h-7 text-orange-400 mx-auto mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-orange-300 mb-2">
                // Studio Tier · ${paywall.price_usd || 19}
              </div>
              <p className="text-zinc-200 text-base mb-4">
                {paywall.message || "Unlock AI Renders for this design."}
              </p>
              {/* V2.0.20 — Required ToS acceptance gates the checkout button. */}
              <div className="mb-4 text-left">
                <TosConsentCheckbox
                  surface="ai_renders_checkout"
                  designId={designId}
                  testIdPrefix="ai-renders-tos-accept"
                  onAccepted={(id) => setTosAcceptanceId(id)}
                  onRevoked={() => setTosAcceptanceId(null)}
                  onError={() =>
                    toast.error("Could not record your acceptance. Please retry.")
                  }
                />
              </div>
              <button
                type="button"
                data-testid="ai-renders-checkout-btn"
                onClick={() => onCheckout?.()}
                disabled={!tosAccepted}
                title={
                  !tosAccepted
                    ? "Please accept the Terms of Service above to continue to payment."
                    : undefined
                }
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-xs uppercase tracking-widest"
              >
                <Sparkles className="w-4 h-4" />
                Unlock Studio · $19
              </button>
              <p className="text-zinc-500 text-xs mt-3">
                One credit · render this design as many photos as you want.
              </p>
            </div>
          )}

          {/* Initial unlock card */}
          {!paywall && !shots && (
            <div className="text-center py-6" data-testid="ai-renders-unlock-card">
              <ImageIcon className="w-10 h-10 text-brass-400 mx-auto mb-3" />
              <p className="text-zinc-300 mb-4">
                Ready to render?{" "}
                {typeof credits === "number" && credits > 0 ? (
                  <span className="text-emerald-400">
                    You have {credits} credit{credits === 1 ? "" : "s"}.
                  </span>
                ) : (
                  <span className="text-zinc-500">Studio credit required.</span>
                )}
              </p>
              {/* V2.0.87 — Reference-image toggle. Renders whenever the
                  design has at least one image upload (thumbnail is a
                  nice-to-have; legacy uploads pre-thumbnails still get
                  the toggle with a file-icon placeholder). */}
              {hasImageUpload && imageUploads.length <= 1 && (
                <label
                  data-testid="ai-renders-reference-toggle"
                  className="mx-auto mb-4 max-w-sm flex items-center gap-3 border border-brass-700/50 hover:border-brass-500 bg-brass-950/20 px-3 py-2.5 cursor-pointer transition-colors text-left"
                >
                  <input
                    type="checkbox"
                    checked={useReference}
                    onChange={(e) => setUseReference(e.target.checked)}
                    data-testid="ai-renders-reference-checkbox"
                    className="accent-brass-500 w-4 h-4 shrink-0"
                  />
                  {referenceThumb ? (
                    <img
                      src={referenceThumb}
                      alt="Uploaded sketch reference"
                      className="w-10 h-10 object-cover border border-brass-700/40 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 grid place-items-center border border-brass-700/40 bg-brass-950/30 shrink-0">
                      <FileImage className="w-4 h-4 text-brass-400/70" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300 mb-0.5">
                      // Use uploaded sketch
                    </div>
                    <div className="text-zinc-400 text-[11px] truncate" title={referenceFilename || ""}>
                      {referenceFilename || "Reference image"}
                    </div>
                  </div>
                  <FileImage className="w-4 h-4 text-brass-400/70 shrink-0" />
                </label>
              )}
              {/* V2.0.87.2 — Multi-image reference selection (max 3).
                  When the design has 2+ image uploads, we surface a
                  selectable grid instead of the single-image toggle. */}
              {hasImageUpload && imageUploads.length > 1 && (
                <div className="mx-auto mb-4 max-w-md text-left" data-testid="ai-renders-reference-multi">
                  <label className="flex items-center gap-2 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useReference}
                      onChange={(e) => setUseReference(e.target.checked)}
                      data-testid="ai-renders-reference-checkbox"
                      className="accent-brass-500 w-4 h-4 shrink-0"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
                      // Use uploaded sketches as reference
                    </span>
                    <span className="font-mono text-[9px] text-zinc-500 ml-auto">
                      {selectedIndices.length}/3 selected
                    </span>
                  </label>
                  <div className={`grid grid-cols-3 gap-2 ${!useReference ? "opacity-40 pointer-events-none" : ""}`}>
                    {imageUploads.slice(-6).reverse().map((img) => {
                      const isSel = selectedIndices.includes(img.index);
                      return (
                        <button
                          key={img.index}
                          type="button"
                          onClick={() => toggleIndex(img.index)}
                          data-testid={`ai-renders-reference-thumb-${img.index}`}
                          title={img.filename}
                          className={
                            "relative border-2 aspect-square overflow-hidden transition-colors " +
                            (isSel
                              ? "border-brass-400 ring-2 ring-brass-500/40"
                              : "border-zinc-800 hover:border-brass-700/60")
                          }
                        >
                          {img.thumb ? (
                            <img
                              src={img.thumb}
                              alt={img.filename}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full grid place-items-center bg-zinc-900">
                              <FileImage className="w-5 h-5 text-brass-400/70" />
                            </div>
                          )}
                          {isSel && (
                            <div className="absolute top-1 right-1 bg-brass-500 text-black rounded-full w-4 h-4 grid place-items-center">
                              <Check className="w-2.5 h-2.5" />
                            </div>
                          )}
                          <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[9px] font-mono truncate px-1 py-0.5">
                            {img.filename}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {imageUploads.length > 6 && (
                    <div className="font-mono text-[9px] text-zinc-500 mt-1.5 text-center">
                      Showing 6 most-recent uploads · {imageUploads.length - 6} older hidden
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                data-testid="ai-renders-generate-btn"
                onClick={handleUnlock}
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brass-600 hover:bg-brass-500 disabled:opacity-50 text-white font-mono text-xs uppercase tracking-widest"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Generate 3 Photos
              </button>
            </div>
          )}

          {/* Shot grid */}
          {shots && (
            <div data-testid="ai-renders-grid" className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                  {fromGallery ? (
                    <>// Saved Gallery · {shots.filter((s) => s.status === "ready").length} of {shots.length} shots</>
                  ) : (
                    <>// {shots.filter((s) => s.status === "ready").length} of {shots.length} renders ready</>
                  )}
                </div>
                {/* V1.7.5 — Regenerate (costs another credit). Only offered
                    when the user is viewing an existing gallery; fresh
                    generations hide it to keep the modal calm.
                    V2.0.87.1 — the reference-image toggle surfaces here
                    too so users can re-render an existing design with
                    (or without) their uploaded sketch. */}
                {fromGallery && hasImageUpload && (
                  <label
                    data-testid="ai-renders-reference-toggle-regen"
                    className="inline-flex items-center gap-1.5 border border-brass-700/50 hover:border-brass-500 bg-brass-950/20 px-2 py-1 cursor-pointer font-mono text-[9px] uppercase tracking-widest text-brass-300 mr-2"
                    title="Thread your uploaded sketch into the regenerate call"
                  >
                    <input
                      type="checkbox"
                      checked={useReference}
                      onChange={(e) => setUseReference(e.target.checked)}
                      data-testid="ai-renders-reference-checkbox-regen"
                      className="accent-brass-500 w-3 h-3 shrink-0"
                    />
                    Use sketch
                  </label>
                )}
                {fromGallery && (
                  <button
                    type="button"
                    data-testid="ai-renders-regenerate-btn"
                    onClick={handleUnlock}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-brass-700 text-brass-300 hover:bg-brass-950/40 hover:border-brass-500 font-mono text-[9px] uppercase tracking-widest disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RotateCw className="w-3 h-3" />
                    )}
                    Regenerate · 1 credit
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {shots.map((s, i) => {
                  const cv = coverState[i] || "idle";
                  return (
                  <div
                    key={s.id || s.label || `shot-${i}`}
                    data-testid={`ai-render-shot-${i}`}
                    data-shot-status={s.status}
                    className="border border-zinc-800 bg-zinc-900/60 p-3"
                  >
                    <div className="aspect-[4/3] grid place-items-center bg-zinc-950 border border-zinc-800 mb-2 overflow-hidden">
                      {s.status === "ready" && s.data_url ? (
                        <img
                          src={s.data_url}
                          alt={s.label}
                          className="object-cover w-full h-full"
                          data-testid={`ai-render-img-${i}`}
                        />
                      ) : s.status === "failed" ? (
                        <div className="flex flex-col items-center gap-1.5 text-rose-500 font-mono text-[9px] uppercase tracking-widest px-2 text-center">
                          <X className="w-5 h-5" />
                          failed
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1.5 text-zinc-600 font-mono text-[9px] uppercase tracking-widest">
                          <Loader2 className="w-5 h-5 animate-spin text-brass-500" />
                          rendering...
                        </div>
                      )}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                      {s.label}
                    </div>
                    <div className="text-zinc-500 text-[10px] mt-0.5 line-clamp-2">{s.prompt}</div>
                    {/* V1.7.3 — Set as Cover button. Available only on
                        ready shots; flips to a green "Cover Set" check
                        once the backend confirms. */}
                    {s.status === "ready" && s.data_url && (
                      <button
                        type="button"
                        onClick={() => handleSetCover(i, s.data_url)}
                        disabled={cv !== "idle"}
                        data-testid={`set-cover-btn-${i}`}
                        className={
                          "mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 font-mono text-[9px] uppercase tracking-widest border transition-colors " +
                          (cv === "saved"
                            ? "border-emerald-500 bg-emerald-950/40 text-emerald-300 cursor-default"
                            : cv === "saving"
                              ? "border-zinc-700 text-zinc-500 cursor-wait"
                              : "border-brass-700 text-brass-300 hover:bg-brass-950/40 hover:border-brass-500")
                        }
                      >
                        {cv === "saved" ? (
                          <>
                            <Check className="w-3 h-3" /> Cover Set
                          </>
                        ) : cv === "saving" ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                          </>
                        ) : (
                          <>
                            <BadgeCheck className="w-3 h-3" /> Set as Cover
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
              {shots.some((s) => s.status === "ready") && (
                <p className="text-emerald-400 text-xs mt-2 text-center font-mono">
                  // Right-click any image to save it.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
